import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { errMsg } from "../lib/api";
import { Brand } from "../components/Layout";
import { Facet } from "../components/kit";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

const HERO = "https://images.unsplash.com/photo-1633934542430-0905ccb5f050?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [eid, setEid] = useState("");
  const [pw, setPw] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await login(eid.trim(), pw);
      navigate(u.must_change_password ? "/change-password" : "/");
    } catch (ex) {
      setErr(errMsg(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAF9F6]">
      <div className="hidden lg:flex relative bs-navy-panel overflow-hidden">
        <img src={HERO} alt="BlueStone jewellery" className="absolute inset-0 w-full h-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#011E38] via-[#011E38]/70 to-transparent" />
        <div className="relative z-10 flex flex-col justify-between p-14 w-full">
          <Brand />
          <div>
            <div className="text-[#C5A059] flex items-center gap-2 bs-eyebrow !text-[#C5A059]"><Facet className="w-3.5 h-3.5" /> Every store. Every day.</div>
            <h1 className="font-display text-5xl font-bold text-white mt-4 leading-[1.05] max-w-lg">Know exactly where you stand.</h1>
            <p className="text-white/60 mt-4 max-w-md">Targets, KPIs and recovery plans for the BlueStone retail network.</p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-7 bs-rise" data-testid="login-form">
          <div className="lg:hidden"><Brand light={false} /></div>
          <div>
            <h2 className="font-display text-4xl font-bold text-[#011E38] tracking-tight">Sign in</h2>
            <p className="text-slate-500 mt-2">Use your Employee ID and password.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="eid">Employee ID</Label>
            <Input id="eid" value={eid} onChange={(e) => setEid(e.target.value)} autoCapitalize="characters" autoComplete="username"
              className="h-14 text-lg" placeholder="e.g. EM1001" data-testid="login-employee-id-input" required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pw">Password</Label>
            <div className="relative">
              <Input id="pw" type={show ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="current-password"
                className="h-14 text-lg pr-12" data-testid="login-password-input" required />
              <button type="button" onClick={() => setShow(!show)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400" data-testid="toggle-password-visibility">
                {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          {err && <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm" data-testid="login-error">{err}</div>}
          <Button type="submit" disabled={busy} className="w-full h-14 text-base rounded-xl bg-[#011E38] hover:bg-[#032C52]" data-testid="login-submit-button">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign in"}
          </Button>
          <p className="text-xs text-slate-400 text-center">Forgot your password? Ask your store manager to reset it.</p>
        </form>
      </div>
    </div>
  );
}
